"use client"

import { motion, AnimatePresence } from "framer-motion"
import { Check, CheckCheck } from "lucide-react"
import { useEffect, useState } from "react"

interface Message {
  id: number
  text: string
  isBot: boolean
  timestamp: string
  status?: "sent" | "delivered" | "read"
}

const conversations: Message[] = [
  { id: 1, text: "Привет! Сколько стоит доставка?", isBot: false, timestamp: "14:23", status: "read" },
  {
    id: 2,
    text: "Здравствуйте! 👋 Доставка по Москве - 300₽, бесплатная от 3000₽",
    isBot: true,
    timestamp: "14:23",
    status: "read",
  },
  { id: 3, text: "А в регионы?", isBot: false, timestamp: "14:24", status: "read" },
  {
    id: 4,
    text: "В регионы 400-600₽ в зависимости от удаленности. Срок доставки 2-4 дня 📦",
    isBot: true,
    timestamp: "14:24",
    status: "read",
  },
  { id: 5, text: "Можно оплатить при получении?", isBot: false, timestamp: "14:25", status: "read" },
  {
    id: 6,
    text: "Да, доступна оплата при получении наличными или картой курьеру 💳",
    isBot: true,
    timestamp: "14:25",
    status: "delivered",
  },
]

export function InstagramMessagesDemo() {
  const [visibleMessages, setVisibleMessages] = useState<Message[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (currentIndex < conversations.length) {
      const timer = setTimeout(
        () => {
          setVisibleMessages((prev) => [...prev, conversations[currentIndex]])
          setCurrentIndex((prev) => prev + 1)
        },
        currentIndex === 0 ? 500 : 1500,
      )

      return () => clearTimeout(timer)
    } else {
      // Restart animation after delay
      const restartTimer = setTimeout(() => {
        setVisibleMessages([])
        setCurrentIndex(0)
      }, 4000)

      return () => clearTimeout(restartTimer)
    }
  }, [currentIndex, visibleMessages])

  return (
    <div className="w-full max-w-4xl mx-auto bg-background rounded-2xl overflow-hidden border-2 border-border shadow-2xl">
      {/* Instagram Header */}
      <div className="bg-background border-b border-border px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F7931E] flex items-center justify-center">
          <span className="text-white font-semibold text-sm">МБ</span>
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Мой Бренд</h3>
          <p className="text-xs text-muted-foreground">Активен(а) сейчас</p>
        </div>
      </div>

      {/* Messages Container */}
      <div className="p-4 min-h-[400px] max-h-[500px] overflow-y-auto bg-background">
        <AnimatePresence>
          {visibleMessages.map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className={`mb-3 flex ${message.isBot ? "justify-start" : "justify-end"}`}
            >
              <div className={`max-w-[75%] ${message.isBot ? "order-2" : "order-1"}`}>
                <div
                  className={`rounded-3xl px-4 py-2.5 text-sm leading-relaxed ${
                    message.isBot
                      ? "bg-[rgba(10,10,10,0.08)] text-foreground rounded-tl-md"
                      : "bg-[#FF6B35] text-white rounded-tr-md"
                  }`}
                >
                  {message.text}
                </div>
                <div className={`flex items-center gap-1 mt-1 px-2 ${message.isBot ? "justify-start" : "justify-end"}`}>
                  <span className="text-[10px] text-muted-foreground">{message.timestamp}</span>
                  {!message.isBot && (
                    <span className="text-muted-foreground">
                      {message.status === "read" ? (
                        <CheckCheck className="w-3 h-3" />
                      ) : message.status === "delivered" ? (
                        <CheckCheck className="w-3 h-3" />
                      ) : (
                        <Check className="w-3 h-3" />
                      )}
                    </span>
                  )}
                </div>
                {message.isBot && index === visibleMessages.length - 1 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 px-2">
                    <span className="text-[10px] text-[#FF6B35] font-medium">⚡ Автоматический ответ InstaSell</span>
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Typing Indicator */}
        {currentIndex < conversations.length && visibleMessages.length > 0 && conversations[currentIndex].isBot && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 mb-3"
          >
            <div className="bg-[rgba(10,10,10,0.08)] rounded-full px-4 py-3 flex items-center gap-1">
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 0.6, repeat: Number.POSITIVE_INFINITY, repeatDelay: 0.2 }}
                className="w-2 h-2 rounded-full bg-muted-foreground"
              />
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 0.6, repeat: Number.POSITIVE_INFINITY, repeatDelay: 0.2, delay: 0.2 }}
                className="w-2 h-2 rounded-full bg-muted-foreground"
              />
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 0.6, repeat: Number.POSITIVE_INFINITY, repeatDelay: 0.2, delay: 0.4 }}
                className="w-2 h-2 rounded-full bg-muted-foreground"
              />
            </div>
          </motion.div>
        )}
      </div>

      {/* Input Field (disabled/demo) */}
      <div className="border-t border-border px-4 py-3 bg-background">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-[rgba(10,10,10,0.05)] rounded-full px-4 py-2 text-sm text-muted-foreground">
            Сообщение...
          </div>
          <button className="text-[#FF6B35] font-semibold text-sm px-3">Отправить</button>
        </div>
      </div>
    </div>
  )
}
